import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Adds an optional second product to the cart alongside the main product.
 *
 * Implementation note: the obvious approach - intercepting the product form's
 * submit and posting both items at once - is not viable in this theme. Its
 * event delegation listens on `document` with `{ capture: true }`
 * (component.js), so the theme's own handler always runs before a listener
 * attached to the form or any element below it; there is no point at which
 * the submit can be pre-empted.
 *
 * So instead of racing it, this waits for the theme to finish its add and
 * reacts to the `cart:update` it dispatches, then adds the upsell and
 * re-broadcasts a cart update carrying freshly rendered cart sections. The
 * theme's own cart drawer/notification renders both states - no cart UI is
 * duplicated here.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} toggle
 * @property {HTMLButtonElement} [infoButton]
 * @extends {Component<Refs>}
 */
export class ProductUpsellComponent extends Component {
  requiredRefs = ['toggle'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** Guards against re-entry while an upsell add is in flight. */
  #pending = false;

  /**
   * Resolves when any in-flight upsell add has settled. Anything that must
   * happen only once the cart is final - notably the skip-to-checkout
   * redirect - awaits this so it can't fire mid-add.
   * @type {Promise<unknown> | null}
   */
  pendingAdd = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Shows/hides the tooltip. Needed for touch devices, where there is no
   * hover - CSS alone covers mouse and keyboard.
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  toggleTooltip(event) {
    // The button sits inside the card's <label>, so without this a tap would
    // also toggle the upsell itself.
    event.preventDefault();
    event.stopPropagation();

    const button = this.refs.infoButton;
    if (!button) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  }

  #closeTooltip() {
    this.refs.infoButton?.setAttribute('aria-expanded', 'false');
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeTooltip();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#closeTooltip();
  };

  /**
   * @param {CustomEvent} event
   */
  #onCartUpdate = (event) => {
    const data = event.detail?.data ?? {};

    // Only react to a successful add made by this page's product form, and
    // never to the cart update this component itself just dispatched.
    if (data.source !== 'product-form-component') return;
    if (data.didError) return;
    if (this.dataset.productId && String(data.productId) !== this.dataset.productId) return;

    const variantId = this.dataset.upsellVariantId;
    if (!variantId || !this.refs.toggle?.checked || this.#pending) return;

    this.#pending = true;
    const quantity = Number(this.dataset.upsellQuantity) || 1;

    this.pendingAdd = this.#addUpsell(variantId, quantity)
      .then((sections) => {
        this.refs.toggle.checked = false;

        // Re-broadcast so the cart drawer re-renders including the upsell.
        document.dispatchEvent(
          new CartAddEvent({}, this.id || 'product-upsell', {
            source: 'product-upsell-component',
            itemCount: quantity,
            sections,
          })
        );
      })
      .catch((error) => {
        // A failed upsell must never disturb the main product's add.
        console.error('Upsell could not be added to cart:', error);
      })
      .finally(() => {
        this.#pending = false;
      });
  };

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @returns {Promise<Record<string, string> | undefined>} Rendered cart sections.
   */
  async #addUpsell(variantId, quantity) {
    // Request the same cart sections the theme asks for, so the drawer can be
    // re-rendered from this response rather than needing another round trip.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const body = { items: [{ id: Number(variantId), quantity }] };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.description || json?.message || `Cart add failed (${response.status})`);
    }

    return json?.sections;
  }
}

if (!customElements.get('product-upsell-component')) {
  customElements.define('product-upsell-component', ProductUpsellComponent);
}
